import type { Brief } from '../lib/brief'

/** A source ref the editor added from the archive search. */
export type AddedRef = { kind: 'member' | 'issue'; id: number }

/** What the list holds: the draft's source ids, plus archive hits added on this screen. */
export type ReviewState = { ids: string[]; added: AddedRef[] }

const refId = (r: AddedRef): string => `${r.kind === 'member' ? 'M' : 'I'}${r.id}`

/**
 * The PATCH body that turns `original` into `current`. `remove` holds source ids that were in
 * the original and are gone now; `include` holds added refs, once each. An added ref whose source
 * is in the original is not sent twice: if it is still kept it is already there, and if it was
 * unticked then re-added it simply is not removed.
 */
export function reviewDiff(original: ReviewState, current: ReviewState): { remove: string[]; include: AddedRef[] } {
  const kept = new Set(current.ids)
  const inOriginal = new Set(original.ids)
  const readded = new Set(current.added.map(refId))
  const remove = original.ids.filter((id) => !kept.has(id) && !readded.has(id))
  const seen = new Set<string>()
  const include: AddedRef[] = []
  for (const r of current.added) {
    const id = refId(r)
    if (seen.has(id) || inOriginal.has(id)) continue
    seen.add(id)
    include.push({ kind: r.kind, id: r.id })
  }
  return { remove, include }
}

export const isDirty = (d: { remove: string[]; include: AddedRef[] }): boolean => d.remove.length > 0 || d.include.length > 0

/** A source chip in plain words. `public` is the title's own free section. */
export function kindLabel(kind: string, sectionLabel: string): string {
  switch (kind) {
    case 'member': return 'Member article'
    case 'issue': return 'Magazine issue'
    case 'public': return sectionLabel
    case 'web': return 'Web'
    default: return kind
  }
}

export const REFERENCE_ONLY_NOTE = 'For links only — not quoted'

const FORMAT_WORD: Record<Brief['format'], string> = { short: 'Short', long: 'Long', deep: 'Deep research', legacy: 'Short' }

/** "6 sources · 4 questions · Deep research". */
export function countsLine(sources: number, questions: number, format: Brief['format']): string {
  return `${sources} ${sources === 1 ? 'source' : 'sources'} · ${questions} ${questions === 1 ? 'question' : 'questions'} · ${FORMAT_WORD[format] ?? FORMAT_WORD.short}`
}

/** Where a source comes from, in a few words: an issue's label, or a web page's host. */
export function sourceWhere(s: { kind: string; url?: string | null; issue_label?: string | null }): string {
  if (s.kind === 'web' && s.url) {
    try { return new URL(s.url).hostname.replace(/^www\./, '') } catch { return s.url }
  }
  return s.issue_label ?? ''
}
